import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useLocation } from 'react-router-dom'

import { buttonClasses } from '@/design-system/Button'
import { ScrollProgress } from '@/design-system/effects'
import { LanguageSwitcher } from '@/design-system/LanguageSwitcher'
import { BottomBarSpacer, Container } from '@/design-system/primitives'
import { usePublicEvent } from '@/features/event/api'
import { WhatsAppLink } from '@/features/event/components/WhatsAppLink'
import { cn } from '@/lib/cn'
import { SUPPORTED_LOCALES } from '@/lib/i18n'

interface NavItem {
  /** Chemin d'une rubrique — les rubriques sont des pages, plus des ancres. */
  to: string
  label: string
}

interface PublicLayoutProps {
  eventName?: string
  logoUrl?: string | null
  availableLocales?: string[]
  // Une page sans rubriques (candidature, commande) garde l'en-tête sans menu.
  navItems?: NavItem[]
  children: React.ReactNode
}

export function PublicLayout({
  eventName,
  logoUrl,
  availableLocales,
  navItems = [],
  children,
}: PublicLayoutProps) {
  // La barre d'achat n'a de sens que sur l'accueil : ailleurs, le visiteur
  // est déjà en train de commander quelque chose.
  const isHome = useLocation().pathname === '/'
  const buyBar = useRef<HTMLDivElement>(null)
  const { t } = useTranslation()
  const { pathname } = useLocation()
  const [menuOpen, setMenuOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)
  const eventQuery = usePublicEvent()

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24)
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  // Le menu mobile occupe l'écran : on empêche la page de défiler derrière.
  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : ''
    return () => {
      document.body.style.overflow = ''
    }
  }, [menuOpen])

  const locales = (availableLocales ?? SUPPORTED_LOCALES).filter((locale) =>
    (SUPPORTED_LOCALES as readonly string[]).includes(locale),
  )

  // Changer de rubrique referme le menu plein écran : sans cela, la nouvelle
  // page s'ouvre cachée derrière le menu resté ouvert.
  useEffect(() => setMenuOpen(false), [pathname])

  return (
    <>
      <ScrollProgress />
      <a
        href="#contenu"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50 focus:rounded-md focus:bg-bg-overlay focus:px-4 focus:py-2"
      >
        {t('nav.concept')}
      </a>

      <header
        className={cn(
          'fixed inset-x-0 top-0 z-40 transition duration-[var(--dur-base)]',
          scrolled && 'glass',
        )}
      >
        <Container className="flex h-16 items-center justify-between gap-4">
          <Link to="/" className="flex min-w-0 items-center gap-3 text-left">
            <img
              src={logoUrl ?? '/leyamo.png'}
              alt=""
              className="h-9 w-9 shrink-0 rounded-full object-cover"
            />
            <span className="font-display text-sm text-content-primary">{eventName ?? 'LeYAMO'}</span>
          </Link>

          <nav aria-label="Principal" className="hidden items-center gap-7 lg:flex">
            {navItems.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                aria-current={pathname === item.to ? 'page' : undefined}
                className={cn(
                  'relative py-1 text-sm transition-colors duration-[var(--dur-base)]',
                  'after:absolute after:inset-x-0 after:-bottom-0.5 after:h-px after:origin-left',
                  'after:bg-[var(--gradient-ember)] after:transition-transform after:duration-[var(--dur-base)]',
                  'after:ease-[var(--ease-out)] hover:text-content-primary hover:after:scale-x-100',
                  pathname === item.to
                    ? 'text-content-primary after:scale-x-100'
                    : 'text-content-secondary after:scale-x-0',
                )}
              >
                {item.label}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <LanguageSwitcher locales={locales} />
            <Link
              to="/billets"
              className={buttonClasses({ size: 'sm', className: 'hidden sm:inline-flex' })}
            >
              {t('cta.ticket')}
            </Link>
            <button
              type="button"
              aria-label={menuOpen ? t('nav.close') : t('nav.open')}
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen((value) => !value)}
              className="flex h-11 w-11 items-center justify-center rounded-full border border-line text-content-primary lg:hidden"
            >
              {menuOpen ? '✕' : '☰'}
            </button>
          </div>
        </Container>
      </header>

      {menuOpen ? (
        <div className="fixed inset-0 z-30 bg-bg-base/95 pt-16 backdrop-blur lg:hidden">
          <Container>
            <nav aria-label="Mobile" className="flex flex-col py-6">
              {navItems.map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  className="border-b border-line-subtle py-5 text-left font-display text-heading-md text-content-primary"
                >
                  {item.label}
                </Link>
              ))}
              <Link to="/billets" className={buttonClasses({ size: 'lg', className: 'mt-8' })}>
                {t('cta.ticket')}
              </Link>
            </nav>
          </Container>
        </div>
      ) : null}

      <main id="contenu">{children}</main>

      <footer className="border-t border-line-subtle py-12">
        <Container className="flex flex-col gap-6 sm:flex-row sm:items-center sm:justify-between">
          <div className="text-sm text-content-muted">
            <p>© {new Date().getFullYear()} LeYAMO — {t('footer.rights')}</p>
            {/* Les documents légaux doivent être atteignables depuis chaque
                page, sans passer par un achat : c'est une obligation, et
                c'est aussi ce qu'on cherche quand on hésite à commander. */}
            <nav className="mt-2 flex flex-wrap gap-x-4 gap-y-1">
              <Link to="/mentions-legales" className="hover:text-content-secondary">
                {t('legal.imprint')}
              </Link>
              <Link to="/cgv" className="hover:text-content-secondary">
                {t('legal.terms')}
              </Link>
              <Link to="/confidentialite" className="hover:text-content-secondary">
                {t('legal.privacy')}
              </Link>
              <Link to="/reglement-concours" className="hover:text-content-secondary">
                {t('legal.contest')}
              </Link>
              {/* Exercer ses droits doit être aussi facile que lire la
                  politique qui les énonce : un clic, depuis n'importe où. */}
              <Link to="/mes-donnees" className="hover:text-content-secondary">
                {t('footer.myData')}
              </Link>
            </nav>
          </div>
          <div className="flex flex-col items-start gap-4 sm:items-end">
            {/* Le pied de page est le seul endroit commun à toutes les pages :
                c'est là qu'on cherche « comment les joindre ». La requête de
                l'édition est déjà en cache — aucun appel de plus. */}
            <WhatsAppLink settings={eventQuery.data?.settings} size="sm" />
            <LanguageSwitcher locales={locales} withLabel />
          </div>
        </Container>
      </footer>

      {/* Sur l'accueil, la barre d'achat collée en bas recouvrait la fin du
          pied de page (« Nous contacter », la langue). Une marge fixe ne
          suivait pas sa hauteur réelle (nom de l'édition, encoche) : on la
          mesure. */}
      {isHome ? <BottomBarSpacer barRef={buyBar} className="sm:hidden" /> : null}

      {/* La barre d'achat ne vit que sur l'accueil.
          Ailleurs, elle poursuivait le visiteur alors qu'il était déjà en
          train de commander autre chose : on lui proposait un billet pendant
          qu'il composait sa table, et le bouton masquait le bas de l'écran. */}
      {isHome ? (
        <div
          ref={buyBar}
          className="glass fixed inset-x-0 bottom-0 z-30 border-t border-line-subtle pb-[env(safe-area-inset-bottom)] sm:hidden"
        >
          <Container className="flex h-16 items-center justify-between gap-4">
            <span className="text-sm text-content-secondary">{eventName}</span>
            <Link to="/billets" className={buttonClasses({ size: 'sm' })}>
              {t('cta.ticket')}
            </Link>
          </Container>
        </div>
      ) : null}
    </>
  )
}
